// SearchFeedbackModal - Captures free-text feedback when a user cannot find
// what they searched for. Posts to /api/catalog/search-events/feedback/ so
// the team can investigate catalogue gaps and search-quality issues.
import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { MessageSquare, X } from 'lucide-react'
import { Button } from './Button'

const MAX_FEEDBACK_LENGTH = 2000

export interface SearchFeedbackModalProps {
  open: boolean
  onClose: () => void
  // The active search term to record alongside the feedback so the team
  // can correlate "feedback for query X" without needing the user to retype.
  query: string
  // Called with the trimmed feedback text once the user clicks Send. Parent
  // owns the API call so it can surface toast notifications consistently
  // with the rest of the app.
  onSubmit: (feedbackText: string) => Promise<void>
}

/**
 * Inner form. Lives as a child of `Dialog.Content` so that when Radix
 * unmounts `Dialog.Content` on close, this component unmounts with it and
 * the draft / submit / error state is reset for free. This replaces the
 * previous `useEffect`-driven reset which the react-hooks lint rule forbids.
 */
function FeedbackForm({
  onClose,
  query,
  onSubmit,
}: Omit<SearchFeedbackModalProps, 'open'>) {
  const [feedbackText, setFeedbackText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const trimmedFeedback = feedbackText.trim()
  const canSubmit = trimmedFeedback.length > 0 && !isSubmitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setErrorMessage(null)
    // Keep `onClose()` outside the try so any post-submit work in the
    // parent (state updates, toasts) can never re-trigger the error UI
    // when the API call itself succeeded. The catch only fires for real
    // submit failures and surfaces the inline error.
    try {
      await onSubmit(trimmedFeedback)
    } catch {
      setErrorMessage('Could not send your feedback. Please try again.')
      setIsSubmitting(false)
      return
    }
    onClose()
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-primary-lightest flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-6 h-6 text-brand-primary" />
          </div>
          <Dialog.Title className="text-lg font-bold text-gray-900">
            Can't find it?
          </Dialog.Title>
        </div>
        <Dialog.Close asChild>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </Dialog.Close>
      </div>

      {/* Content */}
      <div className="px-6 pb-6 space-y-4">
        <Dialog.Description className="text-gray-600 text-sm leading-relaxed">
          Tell us what you were looking for and we'll get back to you.
        </Dialog.Description>

        {query && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Your search</p>
            <p className="text-sm font-semibold text-gray-900 break-words">{query}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="search-feedback-text"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Describe the product
          </label>
          <textarea
            id="search-feedback-text"
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
            placeholder="e.g. 15 mm copper elbow, pack of 10"
            rows={4}
            maxLength={MAX_FEEDBACK_LENGTH}
            disabled={isSubmitting}
            autoFocus
            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-50 disabled:text-gray-500 resize-none"
          />
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-xs text-gray-500">
              {errorMessage ?? 'A brand, size, or part number helps us find it faster.'}
            </p>
            <p className="text-xs text-gray-400 tabular-nums">
              {feedbackText.length}/{MAX_FEEDBACK_LENGTH}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 p-6 pt-0">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="flex-1"
        >
          {isSubmitting ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </>
  )
}

export function SearchFeedbackModal({
  open,
  onClose,
  query,
  onSubmit,
}: SearchFeedbackModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-fade-in" />
        <Dialog.Content
          // Block the Radix default behaviour of closing on overlay click /
          // outside pointer events: a half-written feedback message must not
          // disappear when the user taps off the modal by accident. Escape
          // and the explicit Cancel / X buttons still close it.
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-[calc(100%-2rem)] max-w-md z-[51] animate-slide-up"
        >
          {/* The form lives inside Dialog.Content so Radix's mount/unmount
              on open/close cycle resets the form state automatically — no
              useEffect-driven reset required. */}
          <FeedbackForm onClose={onClose} query={query} onSubmit={onSubmit} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
